import { createFileRoute } from '@tanstack/react-router'

import Breadcrumbs from '@/containers/breadcrumbs'

import { Card, CardHeader } from '@/components/card'
import Content from '@/components/content'
import PageHeader from '@/components/pageHeader'
import { Table, TableBody, TableCell, TableRow } from '@/components/table'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'

import { cn } from '@/utils/cn'

import PlaygroundNavigation from '../-components/playgroundNavigation'

export const Route = createFileRoute('/_authed/$client_code/playground/colours/')({
    component: PlaygroundColours,
})

function PlaygroundColours() {
    const { client_code } = Route.useParams()
    useSetBreadcrumbs([
        {
            to: '/$client_code/playground',
            text: 'Playground',
            params: { client_code },
        },
        'seperator',
        {
            to: '/$client_code/playground/colours',
            text: 'Colours',
            params: { client_code },
        },
    ])

    const baseColours = [
        {
            className: 'bg-rosewater',
            text: 'Rosewater',
        },
        {
            className: 'bg-flamingo',
            text: 'Flamingo',
        },
        {
            className: 'bg-pink',
            text: 'Pink',
        },
        {
            className: 'bg-mauve',
            text: 'Mauve',
        },
        {
            className: 'bg-red',
            text: 'Red',
        },
        {
            className: 'bg-maroon',
            text: 'Maroon',
        },
        {
            className: 'bg-peach',
            text: 'Peach',
        },
        {
            className: 'bg-yellow',
            text: 'Yellow',
        },
        {
            className: 'bg-green',
            text: 'Green',
        },
        {
            className: 'bg-teal',
            text: 'Teal',
        },
        {
            className: 'bg-sky',
            text: 'Sky',
        },
        {
            className: 'bg-sapphire',
            text: 'Sapphire',
        },
        {
            className: 'bg-blue',
            text: 'Blue',
        },
        {
            className: 'bg-lavender',
            text: 'Lavender',
        },
        {
            className: 'bg-text',
            text: 'Text',
        },
        {
            className: 'bg-subtext1',
            text: 'Subtext1',
        },
        {
            className: 'bg-subtext0',
            text: 'Subtext0',
        },
        {
            className: 'bg-overlay2',
            text: 'Overlay2',
        },
        {
            className: 'bg-overlay1',
            text: 'Overlay1',
        },
        {
            className: 'bg-overlay0',
            text: 'Overlay0',
        },
        {
            className: 'bg-surface2',
            text: 'Surface2',
        },
        {
            className: 'bg-surface1',
            text: 'Surface1',
        },
        {
            className: 'bg-surface0',
            text: 'Surface0',
        },
        {
            className: 'bg-base',
            text: 'Base',
        },
        {
            className: 'bg-mantle',
            text: 'Mantle',
        },
        {
            className: 'bg-crust',
            text: 'Crust',
        },
    ]
    const semanticColours = [
        {
            className: 'bg-background',
            text: 'Background',
        },
        {
            className: 'bg-foreground',
            text: 'Foreground',
        },
        {
            className: 'bg-text',
            text: 'Text',
        },
        {
            className: 'bg-accent',
            text: 'Accent',
        },
        {
            className: 'bg-accent-foreground',
            text: 'Accent Foreground',
        },
        {
            className: 'bg-primary',
            text: 'Primary',
        },
        {
            className: 'bg-primary-foreground',
            text: 'Primary Foreground',
        },
        {
            className: 'bg-secondary',
            text: 'Secondary',
        },
        {
            className: 'bg-secondary-foreground',
            text: 'Secondary Foreground',
        },
        {
            className: 'bg-success',
            text: 'Success',
        },
        {
            className: 'bg-warning',
            text: 'Warning',
        },
        {
            className: 'bg-destructive',
            text: 'Destructive',
        },
        {
            className: 'bg-positive-bar',
            text: 'Positive Bar',
        },
        {
            className: 'bg-negative-bar',
            text: 'Negative Bar',
        },
        {
            className: 'bg-card',
            text: 'Card',
        },
        {
            className: 'bg-card-foreground',
            text: 'Card Foreground',
        },
        {
            className: 'bg-popover',
            text: 'Popover',
        },
        {
            className: 'bg-popover-foreground',
            text: 'Popover Foreground',
        },
        {
            className: 'bg-muted',
            text: 'Muted',
        },
        {
            className: 'bg-muted-foreground',
            text: 'Muted Foreground',
        },
        {
            className: 'bg-border',
            text: 'Border',
        },
        {
            className: 'bg-input',
            text: 'Input',
        },
        {
            className: 'bg-ring',
            text: 'Ring',
        },
    ]

    // Chunk up colours into groups of 3
    const baseColourGroups = baseColours.reduce(
        (groups, colour, index) => {
            if (index % 3 === 0) {
                groups.push([])
            }
            groups[groups.length - 1].push(colour)
            return groups
        },
        [] as Array<Array<(typeof baseColours)[number]>>,
    )

    const semanticColourGroups = semanticColours.reduce(
        (groups, colour, index) => {
            if (index % 3 === 0) {
                groups.push([])
            }
            groups[groups.length - 1].push(colour)
            return groups
        },
        [] as Array<Array<(typeof semanticColours)[number]>>,
    )

    return (
        <>
            <Breadcrumbs />
            <Content>
                <PageHeader title="Colours" />
                <PlaygroundNavigation active="colours" />

                <Card className="mb-4">
                    <CardHeader>
                        <h2>Base Colours</h2>
                    </CardHeader>
                    <Table>
                        <TableBody>
                            {baseColourGroups.map(colourGroup => (
                                <TableRow>
                                    {colourGroup.map(colour => (
                                        <TableCell>
                                            <div className="flex items-center">
                                                <div
                                                    className={cn(
                                                        colour.className,
                                                        'mr-4 h-12 w-12 shadow dark:shadow-slate-300',
                                                    )}
                                                />
                                                <p className="flex-grow">{colour.text}</p>
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
                <Card>
                    <CardHeader>
                        <h2>Semantic Colours</h2>
                    </CardHeader>
                    <Table>
                        <TableBody>
                            {semanticColourGroups.map(colourGroup => (
                                <TableRow>
                                    {colourGroup.map(colour => (
                                        <TableCell>
                                            <div className="flex items-center">
                                                <div
                                                    className={cn(
                                                        colour.className,
                                                        'mr-4 h-12 w-12 shadow dark:shadow-slate-300',
                                                    )}
                                                />
                                                <p className="flex-grow">{colour.text}</p>
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            </Content>
        </>
    )
}
